
import {computed, ComputedRef, defineComponent, onMounted, ref, watch,} from "vue";
import {useStore} from "vuex";
import {dispatchError} from "../../utils/error_handling";
import {Content, secondsToCustomFormat} from "./helpers";
import CircularProgressBar from "../../components/CircularProgressBar.vue";
import ContentItem from "./ContentItem.vue";
import {ContentType} from "../../../../domain/entities/content/content";
import {scrollParentToElement} from "../../utils/scroll";

export default defineComponent({
  name: "ContentList",
  components: {ContentItem, CircularProgressBar},
  setup() {
    const store = useStore();
    const contentList = ref<HTMLOListElement | null>(null);

    const contents: ComputedRef<Content> = computed(
        () => store.getters["content_module/contents"]
    );

    const onClickContent = async (content: Content): Promise<void> => {
      try {
        await setCurrentContent(content);
      } catch (e) {
        console.error("[onClickContent] Error setCurrentContent", e);
        await dispatchError(e);
      }
    };

    const onKeyDownContent = async (event: KeyboardEvent, content: Content): Promise<void> => {
      try {
        if (event.key !== " " && event.key !== "Enter") return;
        event.preventDefault();
        await setCurrentContent(content);
      } catch (e) {
        console.error("[onKeyDownContent] Error setCurrentContent", e);
        await dispatchError(e);
      }
    };

    const setCurrentContent = async (content: Content): Promise<void> => {
      try {
        if (!isContentAvailable(content)) return;
        await store.dispatch("content_module/setCurrentContent", content.id);
      } catch (e) {
        console.error("[setCurrentContent] Error dispatch ", e);
        throw e;
      }
    };

    /** Check the content is available to be settled up as the new current content, by checking the content attributes
     * such as, isBlocked, isReleased. */
    const isContentAvailable = (content: Content): boolean => {
      const isAvailable = !content.isBlocked && content.isReleased;
      const isCurrentContent = content.id === currentContent.value?.id;
      return isAvailable && !isCurrentContent;
    };

    const currentContent: ComputedRef<Content | null> = computed(
        () => store.getters["content_module/currentContent"]
    );

    watch(() => currentContent.value, (content) => {
      if (content) onCurrentContentChange(content);
    });

    onMounted(() => {
      if (currentContent.value) {
        onCurrentContentChange(currentContent.value);
      }
    })

    const onCurrentContentChange = (content: Content): void => {
      try {
        const contentListElement = getContentListElement();
        const item = getContentItemByID(content.id);
        scrollParentToElement(contentListElement, item, 44);
      } catch (e) {
        console.error("[onCurrentContentChange] Error ", e);
      }
    };

    const getContentListElement = (): HTMLOListElement => {
      if (!contentList.value) throw new Error("contentList ref is null");
      return contentList.value;
    }

    const getContentItemByID = (contentID: number): HTMLLIElement => {
      const dataAttribute = `[data-item="${contentID}"]`;
      const item = document.querySelector(dataAttribute);
      if (item) return item as HTMLLIElement;
      throw new Error(`Item not found by dataAttribute = ${dataAttribute}`);
    };

    const formatContentLength = (content: Content): string => {
      if (content.type === ContentType.EXERCISE) {
        return `${content.exercise!.numberOfQuestions} perguntas`;
      }

      if (content.type === ContentType.SEARCHFORM) {
        return `${content.searchForm?.numberOfQuestionsForm} perguntas`;
      }

      return secondsToCustomFormat(content.duration, "hh:mm:ss");
    };

    return {
      contentList,
      contents,
      currentContent,
      formatContentLength,
      onClickContent,
      onKeyDownContent,
    };
  }
});
